import * as PIXI from "pixi.js";
import SymbolStripSpinner from "./SymbolStripSpinner";
import {
  registerSkippableSpin,
  releaseSkippableSpin,
} from "./spinSkipRegistry";
import { MYSTERY_SKIP_GROUP } from "./mysterySkip";
import { mysteryDramaSound } from "./mysteryDrama";
import { soundManager } from "../../SoundManager";
import { acquireLoopSound, releaseLoopSound } from "./loopSoundRefs";
import { SFX_CUT_FADE_MS } from "../../sounds";

// The strip loop is REFERENCE COUNTED across every controller that spins
// one — several cells spin at once. See loopSoundRefs.
/**
 * RevealSymbolController — a symbol that contains its own reel.
 *
 * Some symbols are not a single animation. The Mad Labs gamble scatter lands,
 * opens, spins a strip of other symbols inside itself, and settles on one of
 * them. The bonus game has the same shape with different artwork. This runs
 * that sequence for any host spine that provides the pieces.
 *
 * The host spine supplies:
 *   - an EMPTY marker slot, the injection point for the strip
 *   - the animations for each phase
 *   - optionally its own clipping, though see the mask note below
 *
 * Everything specific to a game lives in the config object, so a second symbol
 * with different animation names and different strip contents is a new config,
 * not a new class.
 *
 * The five phases, in order:
 *
 *   1. land    once   the symbol arrives, same as any other symbol
 *   2. (inject)       the strip is built and pinned to the marker slot
 *   3. open    once   doors part, the strip becomes visible
 *   4. loop    loop   plays for as long as the strip is spinning
 *   5. settle  hold   final pose, strip parked on the result
 *
 * MASK: Spine clipping attachments clip the skeleton's own slot rendering.
 * An object added with addSlotObject is a PIXI child positioned by the slot's
 * bone — it is NOT part of that batch, so the skeleton's clipping does not
 * contain it. A PIXI mask sized to the cell is applied here instead. Set
 * `mask: false` in the config if a host spine is ever proven to clip its slot
 * objects.
 */
class RevealSymbolController {
  /**
   * @param {Object}  p
   * @param {Object}  p.spine        Host Spine instance (already created).
   * @param {Object}  p.config       See REVEAL_SYMBOL config shape.
   * @param {number}  p.cellWidth
   * @param {number}  p.cellHeight
   */
  constructor({ spine, config, cellWidth, cellHeight }) {
    this.spine = spine;
    this.config = config;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;

    this.strip = null;
    this.holder = null;
    // Sits inside the holder and undoes the bone chain — see _applyFit.
    this.fit = null;
    this.maskGraphic = null;
    this.destroyed = false;
    this._slotName = null;
    this._activeListener = null;
    this._landedOverlay = null;
    // Set only while the strip's spin clock is running — see _waitForSpin.
    this._skipId = null;
    // Whether THIS controller is holding a share of the looping spin sound.
    this._spinSoundOn = false;
  }

  // ── Spine helpers ────────────────────────────────────────────────────────

  /** First animation name in `candidates` that this skeleton actually has. */
  _resolveAnimation(candidates) {
    const animations = this.spine?.skeleton?.data?.animations;
    if (!animations) return null;
    for (const name of candidates || []) {
      if (animations.find((a) => a.name === name)) return name;
    }
    return null;
  }

  /** First slot name in `candidates` that exists on this skeleton. */
  _resolveSlot(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /**
   * The doors opening, as a sound.
   *
   * Declared by the CONFIG (`openSound`), never assumed here: this class runs
   * every reveal in the game, and only one of them is a door the player is
   * meant to hear. A plain mystery lid opens in silence unless its own config
   * asks otherwise.
   *
   * Fired where the `open` clip STARTS, not where the doors finish parting —
   * the sound has its own attack and would land late against a clip that is
   * already half open.
   */
  _playOpenSound() {
    // ── Whatever was playing UNDER this is cut, not left to ring ──
    //
    // Two things need it, and both for the same reason: the doors take the
    // sequence over, so anything still sounding from the beat before belongs
    // to a moment that has passed.
    //
    //   the regular symbols' win  — a chime that would otherwise ring under
    //                               the doors.
    //   the BUILD-UP              — and this one is not optional. It is 1.5s
    //                               or 3.0s, the player can SKIP straight to
    //                               the doors, and the skip only cuts the
    //                               CLIP. Without this the anticipation plays
    //                               on over an open cell, still building to
    //                               something that has already happened.
    //
    // An array because the build-up is two clips and only the round knows
    // which one ran. fadeOutOneShot is safe on a name that is not playing —
    // it falls through to a plain stop — so cutting both costs nothing.
    //
    // A fade, never a hard stop: SFX_CUT_FADE_MS is 50ms, short enough to read
    // as the doors taking over and long enough not to click.
    const cut = this.config.cutSound;
    for (const name of Array.isArray(cut) ? cut : [cut]) {
      if (name) soundManager.fadeOutOneShot(name, SFX_CUT_FADE_MS);
    }

    // Once per FRAME, not once per cell. A board can hold several mysteries
    // and they all part together — that is one event to the player, and
    // playing it per cell stacks the same 1.1s file on itself. The gate is
    // shared with the build-up, which has the same shape for the same reason.
    const name = this.config.openSound;
    if (name) soundManager.playOncePerFrame(name);
  }

  /**
   * The strip spinning, as a LOOP — the same contract reel_spin has: it starts
   * with the movement and ends when the result settles.
   *
   * Declared by the CONFIG (`spinSound`), for the same reason `openSound` is.
   * Only the gold scatter's strip is meant to be heard; the Golden Mystery
   * spins a strip too, and several of its cells spin at once.
   *
   * `_stopSpinSound` is idempotent and is called from BOTH the normal end of
   * the sequence and from destroy(), because a round cancelled mid-spin must
   * not leave a loop running for ever.
   */
  _startSpinSound() {
    const name = this.config.spinSound;
    if (!name || this._spinSoundOn) return;
    this._spinSoundOn = true;
    acquireLoopSound(name);
  }

  _stopSpinSound() {
    if (!this._spinSoundOn) return;
    this._spinSoundOn = false;
    releaseLoopSound(this.config.spinSound);
  }

  /**
   * Play an animation and resolve when it completes.
   * Resolves immediately when the name does not resolve, so a missing
   * animation skips its phase instead of hanging the whole sequence.
   */
  /**
   * @param {Array|string} candidates
   * @param {object} [skip] Makes this clip skippable while it runs. A press
   *   ENDS it on the frame it arrives — this promise resolves and the caller's
   *   next phase takes the track. Only ever passed for a phase that is pure
   *   waiting; see mysterySkip for why cutting is safe there and what must
   *   never be cut.
   * @param {string} [skip.group] Cancel-together group. See spinSkipRegistry.
   * @param {number} [skip.order] Place in the cascade. 0 for a group.
   */
  /**
   * Does the chosen clip actually carry this event?
   *
   * Asked BEFORE the clip runs, because the answer decides whether the caller
   * may wait for it. Waiting on an event the rig does not have would hold the
   * symbol back until the clip ended — doors opening onto an empty cell.
   *
   * Read off the loaded skeleton rather than a list kept here, so a re-export
   * that drops the event degrades to the old timing on its own.
   *
   * @param {Array|string} candidates The same list `_playOnce` resolves.
   * @param {string} eventName
   */
  _clipHasEvent(candidates, eventName) {
    const name = this._resolveAnimation(candidates);
    if (!name || !eventName) return false;
    const animation = this.spine?.skeleton?.data?.findAnimation?.(name);
    if (!animation?.timelines) return false;
    // An EventTimeline is the only kind carrying `events`, each with a
    // `data.name`. Duck-typed on purpose — importing the runtime class here
    // just to instanceof it would tie this file to a spine version.
    return animation.timelines.some(
      (timeline) =>
        Array.isArray(timeline?.events) &&
        timeline.events.some((event) => event?.data?.name === eventName),
    );
  }

  _playOnce(candidates, skip = null, onEvent = null) {
    const name = this._resolveAnimation(candidates);
    if (!name || !this.spine?.state) return Promise.resolve();

    return new Promise((resolve) => {
      const entry = this.spine.state.setAnimation(0, name, false);
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        // However the clip ended — run out, replaced, destroyed — the entry
        // must come off the list, or a dead cancel stays there and swallows a
        // press. A no-op when the skip already took it.
        releaseSkippableSpin(this._skipId);
        this._skipId = null;
        this.spine.state.removeListener(listener);
        this._activeListener = null;
        resolve();
      };
      const listener = {
        // ── One named spine event, handed back to the caller ──
        //
        // The ANIMATOR marks the frame and the code obeys it, instead of
        // guessing at a time that the next re-export can move. See
        // `openingEvent` in the sequence below — the one thing this is for.
        event: (source, spineEvent) => {
          if (!onEvent || source !== entry) return;
          if (spineEvent?.data?.name !== onEvent.name) return;
          onEvent.fire();
        },
        complete: (completed) => {
          if (completed === entry) finish();
        },
        // A track that gets replaced or interrupted must not leave the
        // sequence waiting for a `complete` that will never arrive.
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      this._activeListener = listener;
      this.spine.state.addListener(listener);

      if (skip && entry) {
        // ── On `this`, not a local ──
        //
        // `destroy()` can only release what it can reach, and it reaches this
        // field. Held in a closure instead, a cell torn down mid-build-up —
        // a resize, an error freeze, the next spin — would leave a dead cancel
        // on the registry, and a dead entry swallows the player's next press
        // without stopping anything.
        //
        // Safe to share with `_waitForSpin`'s id because the two never overlap:
        // the build-up is finished before a strip exists. Released first for
        // the same reason that one is.
        releaseSkippableSpin(this._skipId);
        this._skipId = registerSkippableSpin(
          skip.order ?? 0,
          () => {
            // Already off the list — skipNextSpin deletes before it cancels.
            this._skipId = null;
            // Resolve NOW. The track is left mid-clip on purpose: the caller
            // replaces it synchronously in the phase that follows, so the
            // interrupted pose never reaches a frame.
            finish();
          },
          { group: skip.group },
        );
      }
    });
  }

  /**
   * Hold a pose for good.
   *
   * The settle pose must never be played as a one-shot. `revealed_static` is a
   * ZERO-LENGTH animation: played non-looping it completes on the same frame,
   * the track ends, and Spine reverts the skeleton to its SETUP pose — which
   * for the gamble scatter means the doors snap shut again. Looping a
   * zero-length animation holds its single frame for ever, which is the whole
   * point of a "static" pose.
   */
  _playHold(candidates) {
    return this._playLoop(candidates);
  }

  /** @returns {TrackEntry|false} the entry, so a caller can tune its speed. */
  _playLoop(candidates) {
    const name = this._resolveAnimation(candidates);
    if (!name || !this.spine?.state) return false;
    return this.spine.state.setAnimation(0, name, true) || false;
  }

  /**
   * Resolve at the end of the loop's CURRENT iteration.
   *
   * Spine fires `complete` at every loop boundary, not only at a one-shot's
   * end, so a looping track announces its own seam. That seam is the one place
   * this sequence can change pose without a jolt, because the loop is authored
   * to begin and end in the same state — the gamble scatter's `anticipation`
   * glow runs ffffff00 -> ffffffc1 -> ffffff00 across its 0.5s, so at the
   * boundary it is already invisible. Swapping mid-pulse cuts a lit frame
   * dead, and that is what reads as abrupt.
   *
   * Resolves immediately when nothing is looping, so a config with no loop
   * phase is unaffected.
   */
  _waitForLoopCycle() {
    const state = this.spine?.state;
    const entry = state?.tracks?.[0];
    if (!state || !entry?.loop) return Promise.resolve();

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        state.removeListener(listener);
        if (this._activeListener === listener) this._activeListener = null;
        resolve();
      };
      const listener = {
        complete: (completed) => {
          if (completed === entry) finish();
        },
        // Same guard as _playOnce: a track that is replaced or interrupted
        // must not leave the sequence waiting for a boundary never coming.
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      this._activeListener = listener;
      state.addListener(listener);
    });
  }

  // ── Strip ────────────────────────────────────────────────────────────────

  _injectStrip(result) {
    const stripCfg = this.config.strip;
    this._slotName = this._resolveSlot(this.config.slotNames);
    if (!this._slotName || !stripCfg) {
      console.warn(
        "[RevealSymbol] No injection slot found:",
        this.config.slotNames,
      );
      return false;
    }

    // The holder is what the slot moves; the strip scrolls inside it. Keeping
    // them separate means the spine can scale and translate the whole window
    // (bone `symbol_placeholder` does exactly that) without disturbing the
    // strip's own cell positions.
    this.holder = new PIXI.Container();
    this.holder.name = "reveal_symbol_holder";

    // ── Two containers, because they live in two different spaces ──
    //
    // The HOLDER is what the slot owns. addSlotObject writes the bone's world
    // matrix onto it every frame, so anything we set on it is thrown away —
    // and that matrix carries the whole bone chain, including the `scale`
    // bone every symbol skeleton in this game is built under (1.3).
    //
    // The FIT is ours, and it cancels that chain. Everything below it is
    // therefore in plain cell pixels, which is the unit the strip, the mask
    // and the config's cells are all already written in.
    //
    // Without it the inner symbol came out at
    //     cellWidth x 1.3 x cellHeight / Settings.SYMBOL_HEIGHT
    // — the right size only at one specific cell height, and drifting smaller
    // or larger than its own wrapper at every other. That is why it changed
    // on every resize.
    this.fit = new PIXI.Container();
    this.fit.name = "reveal_symbol_fit";
    this.holder.addChild(this.fit);

    // ── The config IS the spinner's options ──
    //
    // This used to hand-copy a handful of keys, and everything else the config
    // stated was dropped on the floor. `windUpDistance`, `windUpMs` and
    // `landingExitSpeed` never reached the spinner at all — so a strip
    // configured to pull back before it released never did, and its landing was
    // derived by formula while the Golden Mystery's was velocity-matched. The
    // two read identically in the config and moved differently on screen, and
    // no amount of editing the config could have fixed it.
    //
    // Spread, not listed: whatever a config says about motion reaches the
    // spinner, and there is no list to forget. The keys after it are the ones
    // this controller OWNS — geometry, contents, cell drawing — so they come
    // last and win. Extra keys in the block (`buildItems`, `openItem`) are this
    // controller's own vocabulary; the spinner ignores what it does not
    // destructure.
    this.strip = new SymbolStripSpinner({
      ...stripCfg,

      parent: this.fit,
      cellWidth: this.cellWidth,
      cellHeight: this.cellHeight,
      visibleCount: 1,
      // The round's own pool when the config can build one from the result —
      // the strip spins what this round can actually award, not a table.
      // stripCfg.items stays as the static fallback.
      items: stripCfg.buildItems?.(result) || stripCfg.items,
      createCell: stripCfg.createCell,
      renderCell: stripCfg.renderCell,
    });

    // ── The frame the doors open on ──
    //
    // The strip parks a RANDOM cell in the window when it is built, and that is
    // the picture the player sees the moment the doors part. A config that cares
    // which one it is says so — see gambleScatterConfig.strip.openItem, where it
    // is "anything except the blank".
    const opening = stripCfg.openItem?.(this.strip.items);
    if (opening) this.strip.setItem(opening);

    if (this.config.mask !== false) {
      const g = new PIXI.Graphics();
      g.beginFill(0xffffff);
      g.drawRect(
        -this.cellWidth / 2,
        -this.cellHeight / 2,
        this.cellWidth,
        this.cellHeight,
      );
      g.endFill();
      this.fit.addChild(g);
      this.fit.mask = g;
      this.maskGraphic = g;
    }

    this.spine.addSlotObject(this._slotName, this.holder);
    this._applyFit();
    return true;
  }

  /**
   * Re-derive the pool for THIS result on a reused strip.
   *
   * The controller outlives rounds — the strip is injected once and kept —
   * but the pool is the round's own: a second trigger in the same session
   * must not spin over the previous round's variants. The spinner reads
   * `items` at pick time, so swapping the property is the whole job.
   */
  _refreshStripItems(result) {
    const fresh = this.config.strip?.buildItems?.(result);
    if (fresh && this.strip) this.strip.items = fresh;
  }

  /**
   * Make one unit below `fit` equal one cell pixel.
   *
   * The holder inherits two multipliers on its way to the screen:
   *
   *   1. the placeholder bone's world scale, in SKELETON space — the `scale`
   *      bone at 1.3 that every symbol skeleton here is built under;
   *   2. the host spine's own PIXI scale, which the reel sets to
   *      cellHeight / Settings.SYMBOL_HEIGHT.
   *
   * Dividing by both leaves the fit at world scale 1, so a strip cell asked to
   * be `cellWidth` wide is `cellWidth` wide — at every resolution, in every
   * animation, which is the point. Read from the skeleton rather than written
   * as 1.3 so a re-export that changes the rig cannot silently break it again.
   */
  _applyFit() {
    if (!this.fit || !this.spine) return;

    const slot = this.spine.skeleton?.findSlot?.(this._slotName);
    const bone = slot?.bone;
    // bone.a / bone.c are the first column of the bone's world matrix, so
    // their length is its world scale on X.
    const boneScale = bone ? Math.hypot(bone.a, bone.c) : 1;
    const spineScale = this.spine.scale?.x || 1;

    const total = boneScale * spineScale;
    this.fit.scale.set(total ? 1 / total : 1);
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  /**
   * Run the full sequence.
   *
   * @param {*} result The item the strip must land on. Passed straight to
   *                   renderCell, so its shape is the config's business.
   * @returns {Promise<void>} resolves once the settle pose is showing.
   */
  async play(result) {
    if (this.destroyed || !this.spine) return;
    const seq = this.config.sequence || {};

    // ── A reload replays this in full, DELIBERATELY ──
    //
    // The engine's rule for the free-spins intro phase is that the entry
    // sequence replays on every reload until the phase advances. That is
    // written down at the stored-trigger-round block in PopiPlay's initGame,
    // which points at Pot Collection's intro reload doing the same thing. It
    // is also unavoidable in practice: `outcome.screen` is null the moment the
    // package is collected, so the round being replayed IS the stored one.
    //
    // There was a `restored` short-circuit here that skipped to the settled
    // pose. It never ran — no caller ever passed the flag, so it was false in
    // every code path — and it contradicted the rule above. Removed rather
    // than repaired: a game that wants the short version should declare it in
    // its CONFIG as a phase decision, not inherit it from a hidden branch
    // keyed off a field nothing sets.

    // ── A reveal with no strip inside it ──
    //
    // Everything below this branch is the gamble scatter's shape: land, inject
    // a spinner, open, run it, settle on what the backend awarded. The Mystery
    // symbol has no spinner — it lands, builds, opens, and what is behind it
    // was decided before the screen arrived.
    //
    // Entered only when the config declares no strip, so SC1 is untouched. The
    // primitives are the same four the strip path uses.
    if (!this.config.strip) {
      // 1. Land — mystery bounces like any other symbol.
      await this._playOnce(seq.land);
      if (this.destroyed) return;

      // 2. Build up: ONE clip, played ONCE.
      //
      //    Which clip is the round's decision — `anticipationClip` carries it,
      //    and the tiers differ only in what they name. The rig offers a short
      //    standard build-up and a long dramatic one, each authored as a whole
      //    performance with its own arc.
      //
      //    So it is never looped and never sped up. Both of those existed
      //    because there was only ONE clip: a single 0.5s pulse repeated three
      //    times, fast, to stand in for tension it could not express on its
      //    own. With two real clips the repeat is what breaks the effect — it
      //    restarts an arc that was going somewhere.
      //
      //    An empty or unresolvable clip skips the phase, which is what a tier
      //    that wants no build-up asks for. _playOnce resolves immediately
      //    rather than hanging when the clip does not exist.
      //    Skippable, and the ONLY phase of the reveal that is. The waiting is
      //    what the player asked to get past; the doors and the settle are the
      //    reveal itself and play in full. A press ends this clip where it
      //    stands and takes every other mystery on the board with it — they are
      //    one performance. See mysterySkip.
      const clip = this.config.anticipationClip;
      //    The sound the tier chose, under the clip the tier chose. Once per
      //    frame: every mystery on the board starts its build-up in this same
      //    frame and they are ONE performance, so they make one sound.
      soundManager.playOncePerFrame(mysteryDramaSound(clip));
      await this._playOnce(
        clip ? [clip, ...(seq.anticipation || [])] : seq.anticipation,
        { group: MYSTERY_SKIP_GROUP, order: 0 },
      );
      if (this.destroyed) return;

      // 3. Put the revealed symbol INSIDE the window before opening it.
      //
      //    Through onLanded — the SAME hook the strip path uses for its landed
      //    card, so what goes in is a live SPINE and not a flat sprite. That is
      //    what lets the revealed symbol animate inside the window instead of
      //    being a picture the win has to happen somewhere else.
      //
      //    A host that reveals what is UNDERNEATH it declares no onLanded and
      //    nothing is injected — see onOpening below.
      this._slotName = this._resolveSlot(this.config.slotNames);
      if (this._slotName && this.config.onLanded) {
        this.holder = new PIXI.Container();
        this.holder.name = "reveal_symbol_holder";
        this.fit = new PIXI.Container();
        this.fit.name = "reveal_symbol_fit";
        this.holder.addChild(this.fit);
        this.spine.addSlotObject(this._slotName, this.holder);
        this._applyFit();

        this._landedOverlay = this.config.onLanded({
          parent: this.fit,
          item: result,
          cellWidth: this.cellWidth,
          cellHeight: this.cellHeight,
          strip: null,
        });
      }

      // 4. The last moment the cell is still fully covered.
      //
      //    A host that opens onto an INJECTED symbol has nothing to do here.
      //    A host that is only a LID does: whatever it is hiding has to be
      //    standing on the reel before the doors part, or they open onto an
      //    empty cell. The doors start moving 0.03s into `open` and are wide
      //    at 0.5s, so this is the one safe frame for the swap — the caller
      //    owns the reel, this class only knows when to ask.
      //    ── WHEN, exactly: the clip says so, not this file ──
      //
      //    Doing it here, before the clip starts, puts the symbol on the reel
      //    at full strength with the doors still SHUT. A symbol drawn even
      //    slightly wider than its cell then pokes out past the closed doors
      //    and gives the answer away — reported on the computer symbol.
      //
      //    So the rig may name the frame instead. `openingEvent` is a spine
      //    event in the open clip, placed by the animator where the doors have
      //    parted far enough to cover the cell's edges. Move the key, move the
      //    swap; no number in here to keep in sync with an export.
      //
      //    Falls back to the old behaviour whenever the clip does not carry
      //    the event — an older rig, or a re-export that dropped it. Late is a
      //    glitch; NEVER is doors opening onto an empty cell, so the fallback
      //    has to be "now".
      let opened = false;
      const fireOpening = () => {
        if (opened || this.destroyed) return;
        opened = true;
        this.config.onOpening?.(result);
      };

      const openingEvent = this.config.openingEvent;
      const hasEvent =
        !!openingEvent && this._clipHasEvent(seq.open, openingEvent);
      if (!hasEvent) fireOpening();

      // 5. Open, onto the symbol that is now behind the doors.
      //
      //    NOT awaited here. The fade below overlaps the tail of this clip,
      //    so the two run together and the sequence ends when the LID has
      //    gone, not when the timeline runs out.
      this._playOpenSound();
      const opening = this._playOnce(
        seq.open,
        null,
        hasEvent ? { name: openingEvent, fire: fireOpening } : null,
      );
      //    Belt and braces: however the clip ends — run out, replaced, cut —
      //    the symbol must be standing there. A no-op once the event has run.
      opening.then(fireOpening);

      // 6. Fade the host away, when it is a lid rather than a frame.
      //
      //    In CODE and not in the clip, on purpose. The rig is re-exported
      //    regularly and every export has to be hand-repaired; alpha keys we
      //    add to `reveal` are thrown away with it, and they have been twice
      //    already. One tween on the host takes the frame, the doors, the
      //    backing and all twelve lights together, whatever the skeleton
      //    happens to contain.
      //
      //    `fadeOutMs: 0` (the default) leaves the host exactly where it was —
      //    which is what a kept window asks for, and what to set once a rig
      //    fades itself.
      //
      //    ── WHEN it is GONE ──
      //
      //    `fadeOutEndMs` is the moment the host must have finished leaving,
      //    measured from the clip's first frame — not the moment it starts to.
      //    Stated that way round because that is the requirement: the lid is
      //    spent the instant the doors stop moving, and by then there should
      //    be nothing left of it. The fade therefore runs UNDER the opening
      //    doors and lands with them, and changing `fadeOutMs` moves the start
      //    rather than pushing the finish out.
      //
      //    Leave it unset and the fade waits for the whole clip, which is the
      //    right default for any host whose reveal ends where it ends.
      const fadeOutMs = this.config.fadeOutMs ?? 0;
      if (fadeOutMs > 0) {
        const goneBy = this.config.fadeOutEndMs ?? 0;
        if (goneBy > 0) {
          const startAt = Math.max(0, goneBy - fadeOutMs);
          if (startAt > 0) await this._wait(startAt);
        } else {
          await opening;
        }
        if (this.destroyed) return;
        await this._fadeOut(fadeOutMs);
        if (this.destroyed) return;

        // ── Deliberately NOT waiting for the rest of the clip ──
        //
        // The host is invisible from here, so its remaining frames are dead
        // time the whole round would sit through — this promise is what the
        // win cycle waits on. The track is left running and is cleared by the
        // caller's releaseRevealSymbol, which fires `end` and resolves the
        // pending _playOnce; nothing is left dangling either way.
        this._playHold(seq.settle);
        return;
      }

      await opening;
      if (this.destroyed) return;

      // 7. Hold the open pose. NEVER as a one-shot: `revealed_static` is
      //    zero-length, so a non-looping track ends on the same frame and
      //    Spine reverts to the setup pose — doors shut. See _playHold.
      //
      //    A faded lid holds NOTHING — its settle is empty, and a one-shot
      //    `open` keeps applying its own last frame until the caller hands the
      //    spine back.
      this._playHold(seq.settle);
      return;
    }

    // 1. Land — the same arrival every other symbol plays.
    await this._playOnce(seq.land);
    if (this.destroyed) return;

    // 2. Inject once the symbol has settled, not before: building the strip
    //    mid-bounce would show it through the doors as they wobble.
    if (!this.strip && !this._injectStrip(result)) return;
    this._refreshStripItems(result);

    // 3. The DOORS' spin has NO WIND-UP. It starts with them, at once.
    //
    // ── The doors must never part onto a still symbol ──
    //
    // Vladimir, 2026-09-18: the two reveals on this board did not match. The
    // Golden Mystery's lid lifts off a track that is already at speed, so the
    // player sees a chase. This one used to open onto whatever cell happened
    // to be in the window, hold it for the length of the clip, and only then
    // start moving. That still frame reads as a RESULT — the player thinks
    // they have won it — and the spin that follows takes it away again.
    //
    // ── ...but a wind-up is not the way to fix it ──
    //
    // The first answer was to spend the wind-up behind the shut cell and only
    // then open. That works, and it is what shipped on 2026-09-22 — but it
    // costs the whole `windUpMs` before anything happens, and the delay is
    // plainly visible. Vladimir, 2026-09-23: *"i didnt want that i dont like
    // it... just omit the windup, there is no need to delay the animation, when
    // we didnt have windup it worked."*
    //
    // So this spin simply has no wind-up. `start()` reads `windUpDistance` once
    // into `isWindingUp`, so zeroing it for this call is the whole mechanism:
    // the strip accelerates from the first frame and the doors open over it.
    // That is exactly the behaviour the scatter had before its motion was
    // unified with the Golden Mystery's.
    //
    // ── RE-ROLLS ARE NOT TOUCHED ──
    //
    // The value is put straight back, and `respin()` — which every 2nd, 3rd and
    // 4th turn of a blank scatter goes through — reads it fresh on its own
    // `start()`. Only the one spin the doors open on loses it.
    const configuredWindUpDistance = this.strip.windUpDistance;
    this.strip.windUpDistance = 0;

    // 4. Open, and start in the same frame.
    this._playOpenSound();
    const opening = this._playOnce(seq.open);
    this.strip.start();
    this.strip.windUpDistance = configuredWindUpDistance;

    // With the movement, which is now the doors as well — the sound is the
    // strip, and the doors have their own.
    this._startSpinSound();
    const spinStartedAt = performance.now();

    await opening;
    if (this.destroyed) return;

    // 5. The window's own loop, once the doors have finished.
    //
    //    After the open clip and not before: both live on the host's single
    //    track, so starting the loop early would cut the doors off halfway.
    //    Only the STRIP runs early, and it is a separate object.
    this._playLoop(seq.loop);

    // ── The clock runs from the strip, not from the doors ──
    //
    // spinDurationMs is how long the player watches the symbol turn, and that
    // now begins while the doors are still opening. Measuring from here
    // instead would add the whole open clip to every reveal and undo the 1800
    // the +FS was tuned to.
    //
    // Floored at zero: a tab hidden across the doors stops the ticker, so the
    // wall clock can come back with the whole duration already spent. The
    // reveal then lands as the doors finish, which is where it started.
    const spinMs = this.config.spinDurationMs ?? 0;
    const remainingMs = spinMs - (performance.now() - spinStartedAt);
    if (remainingMs > 0) await this._waitForSpin(remainingMs);
    if (this.destroyed) return;

    await this.strip.stopOn(result);
    // The symbol has LANDED — stopOn resolves on the settle, not on the last
    // frame of movement. Before the destroyed check below, because a controller
    // torn down on this beat must still give the loop back. destroy() stops it
    // too; both are safe to run.
    this._stopSpinSound();
    if (this.destroyed) return;

    // ── Texture out, live spine in ──
    //
    // Same split the reels use: a flat texture is enough while the strip is
    // moving, but the landed cell has to become a real skeleton so it can
    // animate. The config owns what that replacement is, because it differs
    // per game; the controller only knows when to ask for it.
    if (this.config.onLanded) {
      this._landedOverlay = this.config.onLanded({
        parent: this.fit,
        item: result,
        cellWidth: this.cellWidth,
        cellHeight: this.cellHeight,
        strip: this.strip,
      });
    }

    // 6. Settle — held, not played once. See _playHold.
    //
    // Held until the loop reaches its own boundary when the config asks for
    // it, so the pose changes on a frame the loop was going to render anyway
    // instead of interrupting a lit one. This costs up to one loop length
    // (0.5s here) and delays THIS PROMISE by the same amount — deliberately,
    // because the caller's celebration then also begins on the seam.
    if (this.config.settleOnLoopComplete) {
      await this._waitForLoopCycle();
      if (this.destroyed) return;
    }
    this._playHold(seq.settle);
  }

  /**
   * Spin the strip AGAIN, inside a window that is already open.
   *
   * ── The doors stay where they are ──
   *
   * The gamble scatter inside a Golden Mystery may land BLANK, and the BE doc
   * says a blank "re-rolls on each following iteration" — so a collector that
   * chains respins asks this cell the same question again. Vladimir,
   * 2026-09-22: *"i dont think we need to touch doors again they are open, we
   * just trigger another spin from that blank position."*
   *
   * Calling `play` again would be the wrong picture twice over: it resets to
   * the setup pose, which is the door SHUT, so the window would close and
   * re-open for a cell the player has been looking into all along.
   *
   * So this is `play`'s spin block and nothing else — refresh the pool, start
   * the strip, wait out the spin, land on the answer, settle. No `land`, no
   * `open`, no fade.
   *
   * The landed picture is dropped first. `onLanded` put a live spine over the
   * strip's cell when it settled, and the strip cannot be seen through it.
   *
   * ── A re-roll is a COIN, the landing is not ──
   *
   * The first reveal is the scatter's own show and keeps its own motion and its
   * own (shorter) length. Every turn after it happens inside a board of coins
   * refilling, so it takes the coins' wind-up, the coins' duration and the
   * coins' place in the stagger: they all start together and finish together.
   * Vladimir, 2026-09-22: *"others are same as coins in same flow they all
   * start same and finish same."*
   *
   * The numbers are the caller's, read from the Golden Mystery's own config, so
   * there is one set for the whole feature and this cannot drift from it.
   *
   * @param   {*} result What it lands on this time.
   * @param   {Object} [spin]
   * @param   {number} [spin.holdMs]         How long it turns before stopping.
   *          Falls back to this symbol's own `spinDurationMs`.
   * @param   {number} [spin.stopOrder]      Its place in the wave. The registry
   *          releases in this order, so a cell stopping ninth must say so.
   * @returns {Promise<boolean>} false when there is nothing to re-spin — no
   *          strip was ever injected, or the controller is gone.
   */
  async respin(result, spin = {}) {
    if (this.destroyed || !this.spine || !this.strip) return false;

    this._landedOverlay?.destroy?.();
    this._landedOverlay = null;

    // ── PUT THE STRIP BACK ON SCREEN ──
    //
    // `onLanded` hides it a frame after the landing — the live card takes the
    // window and the strip behind it is switched off (see gambleScatterConfig).
    // Dropping the card alone therefore leaves the cell EMPTY and the strip
    // turning where nobody can see it: the re-roll ran, reported that it ran,
    // and the player watched a cell stand still while every other cell on the
    // board respun around it. Vladimir, 2026-09-22, with the screenshot to
    // prove it.
    if (this.strip?.container) this.strip.container.visible = true;

    this._refreshStripItems(result);

    const startedAt = performance.now();
    this.strip.start();
    this._startSpinSound();

    // The caller's length when it gives one, this symbol's own when it does
    // not. Measured from the moment the strip starts and floored at zero, the
    // same clock play() uses, so a tab hidden across it lands rather than
    // hangs.
    const spinMs = Number.isFinite(spin.holdMs)
      ? spin.holdMs
      : this.config.spinDurationMs ?? 0;
    const remainingMs = spinMs - (performance.now() - startedAt);
    if (remainingMs > 0) {
      await this._waitForSpin(remainingMs, spin.stopOrder ?? 0);
    }
    if (this.destroyed) return false;

    await this.strip.stopOn(result);
    this._stopSpinSound();
    if (this.destroyed) return false;

    // onLanded hides the strip again a frame later, exactly as it does for the
    // first landing — so the card it builds below is what the cell keeps.
    if (this.config.onLanded) {
      this._landedOverlay = this.config.onLanded({
        parent: this.fit,
        item: result,
        cellWidth: this.cellWidth,
        cellHeight: this.cellHeight,
        strip: this.strip,
      });
    }

    const seq = this.config.sequence || {};
    if (this.config.settleOnLoopComplete) {
      await this._waitForLoopCycle();
      if (this.destroyed) return false;
    }
    this._playHold(seq.settle);
    return true;
  }

  /**
   * The finished cell, with nothing played to get there.
   *
   * play() is the SHOW: land, open, spin, settle. This is the same cell after
   * all of it — the window already open with the awarded package sitting in
   * it — reached in one step because there is nothing left to reveal.
   *
   * The free-spins room needs exactly that. The player won the package in the
   * base game and watched the whole sequence there; arriving in the room, the
   * board they triggered on is put back with the scatter still standing on it,
   * and it must look how they left it. Replaying the reveal would be telling
   * them something they already know, and a bare card with no window around it
   * is not the same cell at all.
   *
   * Deliberately reuses the injection and the hold rather than reimplementing
   * them, so the settled cell here and the settled cell in the base game
   * cannot drift apart: same slot resolution, same holder/fit pair, same
   * _applyFit maths, same onLanded, same held settle pose.
   *
   * @param {*} item What the cell settled on — whatever onLanded expects.
   * @returns {boolean} false when the spine has no slot to inject into.
   */
  showSettled(item) {
    if (this.destroyed || !this.spine) return false;

    this._slotName = this._resolveSlot(this.config.slotNames);
    if (!this._slotName) {
      console.warn(
        "[RevealSymbol] No injection slot found:",
        this.config.slotNames,
      );
      return false;
    }

    // The same two containers _injectStrip builds, and for the same reason:
    // the holder is the slot's to move, the fit cancels the bone chain so
    // everything under it is in plain cell pixels. No strip and no mask — the
    // window is not going to scroll.
    this.holder = new PIXI.Container();
    this.holder.name = "reveal_symbol_holder";
    this.fit = new PIXI.Container();
    this.fit.name = "reveal_symbol_fit";
    this.holder.addChild(this.fit);

    this.spine.addSlotObject(this._slotName, this.holder);
    this._applyFit();

    if (this.config.onLanded) {
      this._landedOverlay = this.config.onLanded({
        parent: this.fit,
        item,
        cellWidth: this.cellWidth,
        cellHeight: this.cellHeight,
        strip: null,
        // A COPY of a card that already landed, not an arrival — so no
        // landing clip. Vladimir, 2026-09-25: the room's card played its
        // `bounce` behind the wipe and the glow flew as the wipe cleared; "can
        // we just copy the standing animation with open doors and symbol
        // inside and then shoot pips".
        settled: true,
      });
    }

    // Held, never played once — `revealed_static` is zero length, so a
    // non-looping track ends on the frame it starts and Spine reverts to the
    // setup pose, which for this skeleton is the doors SHUT over the card.
    this._playHold(this.config.sequence?.settle);
    return true;
  }

  /**
   * The win pose, held on its LAST frame.
   *
   * playWin LOOPS the celebration, which is right while the trigger line is
   * running and the player is waiting for the feature to take the screen.
   * A restored cell is not waiting for anything — the round is already being
   * played — so it wants the same pose without the movement.
   *
   * The last frame rather than a separate "static" clip, because `win` is what
   * ATTACHES this cell's finished state: the gold backing and gold frame are
   * keyed on by it and appear nowhere in `revealed_static`, which is the pose
   * a BLANK rests on. Freezing at the end keeps every one of those and drops
   * only the bounce — the clip's own translate returns to zero on its final
   * key, so the frozen frame is the resting position.
   *
   * @returns {boolean} false when the config declares no win clip.
   */
  restOnWinEnd() {
    if (this.destroyed || !this.spine?.state) return false;

    const name = this._resolveAnimation(this.config.sequence?.win);
    if (!name) return false;

    const clip = this.spine.skeleton?.data?.findAnimation?.(name);
    const entry = this.spine.state.setAnimation(0, name, false);
    if (!entry) return false;

    // Placed at the end and kept alive. Without trackEnd the entry is disposed
    // the moment it passes its duration and the skeleton drops to setup pose,
    // which for this spine is the doors shut over the card.
    entry.trackEnd = Number.MAX_VALUE;
    entry.trackTime = clip?.duration ?? 0;
    return true;
  }

  /**
   * Celebrate, once the strip has landed.
   *
   * Deliberately NOT part of play(). The reveal is one symbol on a winning
   * line, and the other symbols on that line have to celebrate at the same
   * moment — so the caller owns the timing and simply asks all of them
   * together. See useWinController's revealPromise block.
   *
   * Looped, not one-shot, for the same reason the other trigger symbols loop:
   * the line runs until the intro dialog takes the screen, and a one-shot
   * would end its track and drop the skeleton back to its setup pose, which
   * for this spine means the doors snap shut over the card.
   */
  playWin() {
    if (this.destroyed) return false;
    return this._playLoop(this.config.sequence?.win);
  }

  // ── A settled cell that is still alive ────────────────────────────────────
  //
  // The mystery does not hand its cell back. It opens, keeps its frame, and
  // the symbol standing inside the frame is what celebrates when a win line
  // reaches it — many times, as the display cycles. So the host holds one pose
  // for the whole round and every change of state happens on the INJECTED
  // object, which is why onLanded is required to be a spine.

  /** Put the host back on its settled pose. Held, never played once. */
  holdSettled() {
    if (this.destroyed) return false;
    return !!this._playHold(this.config.sequence?.settle);
  }

  /**
   * Celebrate INSIDE the window.
   *
   * @param   {number} loops 0 = for ever, N = N times then hold.
   * @returns {boolean} false when the injected object cannot celebrate.
   */
  playInnerWin(loops = 0) {
    if (this.destroyed) return false;
    return this._landedOverlay?.playWin?.(loops) ?? false;
  }

  /** Back to the injected object's resting pose. */
  playInnerIdle() {
    if (this.destroyed) return false;
    return this._landedOverlay?.playIdle?.() ?? false;
  }

  /** Let the injected object tick, or park it. See setAwake. */
  setInnerAwake(awake) {
    if (this.destroyed) return false;
    return this._landedOverlay?.setAwake?.(awake) ?? false;
  }

  /**
   * Take the host spine to alpha 0 over `ms`, and resolve there.
   *
   * The whole skeleton at once, because that is the point: a lid has to leave
   * completely, and picking its parts off one alpha timeline at a time is what
   * the animator's export keeps undoing.
   *
   * Nothing puts the alpha back here. The pooled spine is handed to
   * releaseRevealSymbol immediately after, which hides it and resets the pose,
   * and every path that shows a pooled spine again sets alpha 1 first.
   *
   * @param   {number} ms 0 or less leaves the spine untouched.
   * @returns {Promise<void>}
   */
  _fadeOut(ms) {
    const spine = this.spine;
    if (!spine || !(ms > 0)) return Promise.resolve();
    return new Promise((resolve) => {
      const from = spine.alpha ?? 1;
      let elapsed = 0;
      const tick = () => {
        // A destroyed controller has already given the spine back — writing an
        // alpha onto it here would fight whatever took it over.
        if (this.destroyed || spine.destroyed) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
          return;
        }
        elapsed += PIXI.Ticker.shared.deltaMS;
        const t = Math.min(1, elapsed / ms);
        spine.alpha = from * (1 - t);
        if (t >= 1) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
        }
      };
      PIXI.Ticker.shared.add(tick);
    });
  }

  _wait(ms) {
    return new Promise((resolve) => {
      let remaining = ms;
      const tick = () => {
        if (this.destroyed) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
          return;
        }
        remaining -= PIXI.Ticker.shared.deltaMS;
        if (remaining <= 0) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
        }
      };
      PIXI.Ticker.shared.add(tick);
    });
  }

  /**
   * The strip's spin clock — and the ONLY thing in this sequence a player may
   * cut short.
   *
   * Identical to _wait except that it puts itself on the skip registry while
   * it runs, so a tap can end it early — unless the config says otherwise.
   *
   * ── `spinSkippable: false` ──
   *
   * A reveal that runs BESIDE the board rather than in front of it must not
   * answer the player's press. The +FS scatter is the case: its window opens
   * inside a Golden Mystery that is still running, and the rest of the board
   * carries on around it. A tap there reads as "stop the round", and cutting
   * this one strip short while its neighbours keep spinning looks like a
   * fault rather than a slam stop.
   *
   * Nothing else changes. The strip still lands through stopOn(result) at the
   * end of its own duration, so the reveal simply plays out.
   *
   * Default is skippable, which is the gamble scatter's behaviour and the one
   * GDD §13 asks for: that reveal owns the screen, and a press there is the
   * player asking for their answer sooner. Everything after it is untouched: the
   * strip still lands through stopOn(result), so a skip decides WHEN the
   * landing starts, never where it ends, and the result cannot be changed by
   * skipping.
   *
   * There is one reveal per cell, so the cascade order is 0 — a lone entry on
   * the registry is always the next one to go.
   */
  _waitForSpin(ms, stopOrder = 0) {
    return new Promise((resolve) => {
      let remaining = ms;
      const finish = () => {
        PIXI.Ticker.shared.remove(tick);
        releaseSkippableSpin(this._skipId);
        this._skipId = null;
        resolve();
      };
      const tick = () => {
        if (this.destroyed) {
          finish();
          return;
        }
        remaining -= PIXI.Ticker.shared.deltaMS;
        if (remaining <= 0) finish();
      };
      // Never hold two entries. A second register without a release would
      // strand the first on the list, and a stranded entry swallows a press
      // without stopping anything.
      releaseSkippableSpin(this._skipId);
      this._skipId = null;
      if (this.config?.spinSkippable !== false) {
        // ── `stopOrder` is NOT decoration ──
        //
        // The same fault GoldenMysteryCellController already fixed, which this
        // controller never got. Registering every wait at 0 puts it at the
        // FRONT of the registry's queue, so a cell that should stop ninth in a
        // wave is released with the first one. On a gamble scatter re-rolling
        // inside a board of respinning coins that is exactly what happened: it
        // was told to spin for 7840ms and landed after 1600.
        this._skipId = registerSkippableSpin(stopOrder, finish);
      }
      PIXI.Ticker.shared.add(tick);
    });
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  resize(cellWidth, cellHeight) {
    // A zero size is the layout still measuring, not a cell — the same refusal
    // GoldenMysteryCellController and SymbolStripSpinner carry, and this class
    // was the one place in the family without it.
    //
    // The STRIP was already safe: it refuses bad input itself. What was not is
    // everything this method owns — `cellWidth`/`cellHeight` were stored as 0,
    // and the mask below was redrawn as a 0x0 rect, which hides the cell
    // outright until a real size arrives. Cheap to refuse, and it keeps the
    // zero away from `_landedOverlay.resize` as well.
    if (
      !Number.isFinite(cellWidth) ||
      !Number.isFinite(cellHeight) ||
      cellWidth <= 0 ||
      cellHeight <= 0
    ) {
      return;
    }

    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    // The host spine is rescaled on every dimension change, so the layer that
    // cancels it has to be recomputed before anything below is measured.
    this._applyFit();
    this.strip?.resize(cellWidth, cellHeight);
    // The landed card has to follow a resize too.
    this._landedOverlay?.resize?.(cellWidth, cellHeight);
    if (this.maskGraphic) {
      this.maskGraphic.clear();
      this.maskGraphic.beginFill(0xffffff);
      this.maskGraphic.drawRect(
        -cellWidth / 2,
        -cellHeight / 2,
        cellWidth,
        cellHeight,
      );
      this.maskGraphic.endFill();
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    // Off the registry immediately. A dead entry left on the list would
    // swallow the player's next press without stopping anything.
    releaseSkippableSpin(this._skipId);
    this._skipId = null;
    // A round cancelled mid-spin must not leave the loop running for ever.
    // No-op when the sequence already ended normally.
    this._stopSpinSound();

    if (this._activeListener && this.spine?.state) {
      this.spine.state.removeListener(this._activeListener);
      this._activeListener = null;
    }
    if (this._slotName && this.spine?.removeSlotObject) {
      try {
        this.spine.removeSlotObject(this._slotName, this.holder);
      } catch {
        // Slot object already gone with the spine — nothing to detach.
      }
    }
    this._landedOverlay?.destroy?.();
    this._landedOverlay = null;
    this.strip?.destroy();
    this.strip = null;
    if (this.holder) {
      // Drop the mask before the destroy — the holder takes the fit down with
      // it, and a mask still pointing at a destroyed Graphics is a crash.
      if (this.fit) this.fit.mask = null;
      this.holder.destroy({ children: true });
      this.holder = null;
      this.fit = null;
    }
    this.maskGraphic = null;
  }
}

export default RevealSymbolController;
